import React, { useState } from 'react';
import { RotateCw } from 'lucide-react';
import AdvertiserSelector from '../organization/AdvertiserSelector';
import ConfigureStep from '@/components/creatives/ConfigureStep';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

const BriefPhase = ({
    data,
    onSave,
    loading,
    onPhotoToggle,
    onPhotoUpload,
    onPhotoDelete,
    onReviewToggle,
    characters = [],
    onCharacterUpload,
    onCharacterUpdate,
    onCharacterDelete,
    onLogoUpload,
    onLogoDelete,
    onRefresh,
    isRefreshing,
    videoConfigLoading = false,
    aspectRatio,
    onAspectRatioChange,
    duration,
    onDurationChange,
    scriptMode,
    onScriptModeChange,
    voiceId,
    onVoiceChange,
    logoOverlayEnabled,
    logoOverlayPosition,
    logoOverlayOpacity,
    onLogoOverlayChange,
    onLogoOverlayOpacityPreview,
    photoUploadLoading = false,
    photoDeleteLoading = {},
    campaignBrief,
    onCampaignBriefChange,
    campaignOptions,
    selectedAdvertiserId,
    onAdvertiserChange,
    showAdvertiserSelector = false,
}) => {
    const [showRefreshModal, setShowRefreshModal] = useState(false);

    if (!data) {
        return (
            <div className="flex min-h-0 flex-1 items-center justify-center bg-gradient-to-br from-background to-muted/40">
                <div className="text-center">
                    <Spinner size="lg" />
                    <p className="mt-4 text-muted-foreground">Loading Business Brief...</p>
                </div>
            </div>
        );
    }

    const selectedReviewIds = (data.reviews || []).reduce((acc, review, index) => {
        const reviewId = typeof review === 'object' && review.review_id ? review.review_id : `review-${index}`;
        if (typeof review !== 'object' || review.selected !== false) {
            acc.push(reviewId);
        }
        return acc;
    }, []);

    return (
        <>
            <ConfigureStep
                realBusinessData={data}
                currentOrg={null}
                aspectRatio={aspectRatio}
                setAspectRatio={onAspectRatioChange}
                duration={duration}
                setDuration={onDurationChange}
                scriptMode={scriptMode}
                setScriptMode={onScriptModeChange}
                voiceId={voiceId}
                setVoiceId={onVoiceChange}
                silentVideo={false}
                setSilentVideo={() => {}}
                campaignBrief={campaignBrief}
                setCampaignBrief={onCampaignBriefChange}
                campaignOptions={campaignOptions}
                photos={data.photos || []}
                isUploadingPhoto={photoUploadLoading}
                photoDeleteLoading={photoDeleteLoading}
                onPhotoToggle={onPhotoToggle}
                onPhotoUpload={onPhotoUpload}
                onPhotoDelete={onPhotoDelete}
                reviews={data.reviews || []}
                selectedReviews={selectedReviewIds}
                onReviewToggle={onReviewToggle}
                characters={characters}
                onCharacterUpload={onCharacterUpload}
                onCharacterUpdate={onCharacterUpdate}
                onCharacterDelete={onCharacterDelete}
                logoDataUrl={
                    data.logo_data_url
                    || data.brand_identity?.logo_data_url
                    || data.brand_identity?.logo_url
                    || null
                }
                onLogoUpload={onLogoUpload}
                onLogoDelete={onLogoDelete}
                logoOverlayEnabled={logoOverlayEnabled}
                setLogoOverlayEnabled={(enabled) => onLogoOverlayChange?.({ enabled })}
                logoOverlayPosition={logoOverlayPosition}
                setLogoOverlayPosition={(position) => onLogoOverlayChange?.({ position })}
                logoOverlayOpacity={logoOverlayOpacity}
                setLogoOverlayOpacity={(opacity) => onLogoOverlayChange?.({ opacity })}
                onGenerate={onSave}
                title="The Creative Brief"
                description="Review the shared creative setup before entering Director Studio."
                headerActions={onRefresh ? (
                    <Button
                        variant="outline"
                        onClick={() => setShowRefreshModal(true)}
                        disabled={isRefreshing}
                    >
                        <RotateCw className={cn('mr-2 h-4 w-4', isRefreshing && 'animate-spin')} />
                        Refresh Data
                    </Button>
                ) : null}
                campaignBriefTopSlot={showAdvertiserSelector ? (
                    <div className="space-y-2">
                        <Label>Advertiser</Label>
                        <AdvertiserSelector
                            value={selectedAdvertiserId}
                            onChange={onAdvertiserChange}
                            placeholder="Select or create advertiser..."
                        />
                        <p className="text-xs text-muted-foreground">
                            Select the advertiser this video is for
                        </p>
                    </div>
                ) : null}
                hideBackButton
                minimumSelectedPhotos={0}
                allowZeroSelectedPhotos={false}
                generateButtonLabel="Approve & Enter Studio"
                generateButtonLoadingLabel="Saving Brief..."
                isGenerating={loading}
                videoSettingsDisabled={videoConfigLoading}
                logoSectionProps={{
                    emptyStateMessage: 'No logo detected. Click "Refresh Data" to attempt logo detection.',
                    allowOverlayWithoutLogo: true,
                    onLogoOverlayOpacityPreview,
                    disabled: videoConfigLoading,
                }}
                layoutFullHeight
                layoutStickyOffsetClassName="top-0"
            />

            <Dialog open={showRefreshModal} onOpenChange={setShowRefreshModal}>
                <DialogContent className="sm:max-w-[420px]">
                    <DialogHeader>
                        <DialogTitle>Refresh Business Data?</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-muted-foreground">
                        This will reload all business data from the source. Any unsaved changes you made to the brief will be lost.
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowRefreshModal(false)}>Cancel</Button>
                        <Button
                            onClick={() => {
                                setShowRefreshModal(false);
                                onRefresh?.();
                            }}
                        >
                            Refresh
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default BriefPhase;
